import { MouseEvent, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { Checkbox, Icon, Menu } from '@atom/mui';
import { DeleteIcon, FileDownloadIcon, StarIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { mediaFileName, mediaUrl } from '@atom/utilities/mediaUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import classes from './photosTab.module.css';

const { MenuItem } = Menu;

const styles = {
  coverPhotoIcon: {
    color: colors.brand.blue,
    paddingRight: '0.5rem',
  },
  checkbox: {
    color: colors.neutral.white,
  },
};

export type PhotoTileProps = {
  media: Media;
  isCoverPhoto: boolean;
  isSelected: boolean;
  deleteLoading: boolean;
  canMarkAsCoverPhoto: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
  onOpen: (id: string) => void;
  onSelectionToggle: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => Promise<void>;
  onCoverPhotoToggle: (id: string) => void;
};

const PhotoTile = ({
  media,
  isCoverPhoto,
  isSelected,
  deleteLoading,
  canMarkAsCoverPhoto,
  canUpdateMedia,
  canDeleteMedia,
  onOpen,
  onSelectionToggle,
  onRename,
  onDelete,
  onCoverPhotoToggle,
}: PhotoTileProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);

  const handleDelete = async () => {
    await onDelete(media.id);
    setDeleteModalOpen(false);
  };

  const handleSelectionToggle = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onSelectionToggle(media.id);
  };

  const coverPhotoText = isCoverPhoto
    ? 'Unmark as cover photo'
    : 'Mark as cover photo';
  const coverPhotoColor = isCoverPhoto
    ? colors.brand.blue
    : colors.neutral.gray;

  const styleOverrides = {
    tileImage: {
      backgroundImage: `url('${mediaUrl(media.fileId, 180)}')`,
    },
  };

  return (
    <>
      <div className={classes.photoTile} data-cy={`photoTile-${media.name}`}>
        <div
          className={classes.tileImageContainer}
          style={styleOverrides.tileImage}
        >
          <div
            className={clsx(
              classes.selectionMask,
              isSelected && classes.selected,
            )}
            onClick={() => onOpen(media.id)}
            data-cy={`photoTileImage-${media.name}`}
          >
            <div
              className={classes.selectionCheckbox}
              onClick={handleSelectionToggle}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => {}}
                style={styles.checkbox}
                data-cy={`photoTileCheckbox-${media.name}`}
              />
            </div>
          </div>
        </div>
        <div className={classes.imageInfoContainer}>
          <div className={classes.imageTitleContainer}>
            {isCoverPhoto ? (
              <Icon style={styles.coverPhotoIcon} icon={StarIcon} />
            ) : (
              <div />
            )}
            <div className={classes.imageName}>{mediaFileName(media)}</div>
            <Menu
              noMinWidth
              IconButtonProps={{ size: 'small' }}
              dataCyLabel={`photoTileMenu-${media.name}`}
            >
              {canUpdateMedia && (
                <MenuItem
                  onClick={() => setRenameModalOpen(true)}
                  startAdornment={<img src={renameIcon} />}
                  data-cy="photoTileRenameButton"
                >
                  Rename
                </MenuItem>
              )}
              <a href={mediaUrl(media.fileId)} download={mediaFileName(media)}>
                <MenuItem
                  startAdornment={<Icon icon={FileDownloadIcon} />}
                  data-cy="photoTileDownloadButton"
                >
                  Download
                </MenuItem>
              </a>
              {canMarkAsCoverPhoto && (
                <MenuItem
                  onClick={() => onCoverPhotoToggle(media.id)}
                  startAdornment={
                    <Icon color={coverPhotoColor} icon={StarIcon} />
                  }
                  data-cy="photoTileCoverPhotoButton"
                >
                  {coverPhotoText}
                </MenuItem>
              )}
              {canDeleteMedia && (
                <MenuItem
                  onClick={() => setDeleteModalOpen(true)}
                  startAdornment={<Icon icon={DeleteIcon} />}
                  data-cy="photoTileDeleteButton"
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </div>
          <div className={classes.additionalInfoContainer}>
            <div>
              {!R.isEmpty(media.createdBy)
                ? textDisplayUtilities.displayName(media.createdBy)
                : ''}
            </div>
            <div>{`Uploaded on ${setDisplayDate(media.createdDate)}`}</div>
            <div>{`Captured on ${setDisplayDate(media.capturedDate)}`}</div>
          </div>
        </div>
      </div>
      <RenameModal
        id={media.id}
        name={media.name}
        type={media.type}
        renameAction={onRename}
        open={renameModalOpen}
        closeModal={() => setRenameModalOpen(false)}
      />
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        title={`Delete ${textDisplayUtilities.capitalize(media.type)}`}
        content={`Are you sure you want to delete this ${media.type}?`}
        loading={deleteLoading}
      />
    </>
  );
};

export default PhotoTile;
